/**
 * BaremesListePage
 * Liste des barèmes ITS avec leurs tranches
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, ChevronDown, ChevronRight, CheckCircle2, Clock, Pencil } from 'lucide-react';
import { configurationApi } from '../api/configuration.api';
import { IconButton } from '../../../shared/components/ui/IconButton';

interface TrancheITS {
  idTranche: string;
  ordre: number;
  seuilMin: number;
  seuilMax: number | null;
  taux: number;
  deductionFixe: number;
  libelle?: string;
}

interface ConfigITS {
  idConfigITS: string;
  nom: string;
  description?: string;
  dateDebut: string;
  dateFin?: string | null;
  actif?: boolean;
  tranches: TrancheITS[];
}

function formatMontant(v: number): string {
  return v.toLocaleString('fr-FR') + ' F';
}

function formatTaux(v: number): string {
  return (v * 100).toFixed(1) + ' %';
}

function estActif(config: ConfigITS): boolean {
  const today = new Date();
  const debut = new Date(config.dateDebut);
  if (debut > today) return false;
  if (config.dateFin && new Date(config.dateFin) < today) return false;
  return true;
}

function TranchesTable({ tranches }: { tranches: TrancheITS[] }) {
  return (
    <div className="mt-3 rounded-lg overflow-hidden border border-primary bg-primary-light">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-primary-light text-primary-text">
            <th className="px-3 py-2 text-left font-semibold">Ordre</th>
            <th className="px-3 py-2 text-right font-semibold">Seuil min</th>
            <th className="px-3 py-2 text-right font-semibold">Seuil max</th>
            <th className="px-3 py-2 text-right font-semibold">Taux</th>
            <th className="px-3 py-2 text-right font-semibold">Déduction fixe</th>
            <th className="px-3 py-2 text-left font-semibold">Libellé</th>
          </tr>
        </thead>
        <tbody>
          {tranches.map((t, i) => (
            <tr key={t.idTranche} className={i % 2 === 0 ? 'bg-surface' : 'bg-primary-light'}>
              <td className="px-3 py-2 text-content-muted">{t.ordre}</td>
              <td className="px-3 py-2 text-right text-content-muted font-mono">{formatMontant(Number(t.seuilMin))}</td>
              <td className="px-3 py-2 text-right text-content-muted font-mono">
                {t.seuilMax !== null ? formatMontant(Number(t.seuilMax)) : '∞'}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-primary-text">{formatTaux(Number(t.taux))}</td>
              <td className="px-3 py-2 text-right text-content-muted font-mono">{formatMontant(Number(t.deductionFixe))}</td>
              <td className="px-3 py-2 text-content-subtle">{t.libelle || `Tranche ${t.ordre}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BaremesListePage() {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: baremes = [], isLoading, error } = useQuery<ConfigITS[]>({
    queryKey: ['baremes-its'],
    queryFn: configurationApi.obtenirITS,
  });

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-content-subtle text-sm">
        Chargement des barèmes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-danger-light border border-danger p-4 text-danger text-sm">
        Erreur lors du chargement des barèmes ITS.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-content">Barèmes ITS</h1>
          <p className="text-sm text-content-subtle mt-1">
            Impôt sur les traitements et salaires — barèmes progressifs
          </p>
        </div>
        <Link
          to="/paie/configuration/baremes/nouveau"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-content-inverted text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors"
        >
          <Plus className="w-4 h-4" />
          Créer un barème
        </Link>
      </div>

      {/* Liste */}
      {baremes.length === 0 ? (
        <div className="text-center py-16 text-content-subtle text-sm">
          Aucun barème ITS configuré.{' '}
          <Link to="/paie/configuration/baremes/nouveau" className="text-primary-text hover:underline">
            Créer le premier
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {baremes.map(b => {
            const actif = estActif(b);
            const open = expanded.has(b.idConfigITS);

            return (
              <div
                key={b.idConfigITS}
                className={`rounded-xl border bg-surface shadow-sm overflow-hidden transition-all
                  ${actif ? 'border-l-4 border-l-primary border-border' : 'border-border'}`}
              >
                {/* Ligne principale */}
                <div className="flex items-center gap-4 px-5 py-4">
                  {/* Statut */}
                  <div className="flex-shrink-0">
                    {actif ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-success bg-success-light px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Actif
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs font-medium text-content-subtle bg-surface-overlay px-2 py-1 rounded-full">
                        <Clock className="w-3.5 h-3.5" />
                        Inactif
                      </span>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-content truncate">{b.nom}</p>
                    <p className="text-xs text-content-subtle mt-0.5">
                      Du {new Date(b.dateDebut).toLocaleDateString('fr-FR')}
                      {b.dateFin ? ` au ${new Date(b.dateFin).toLocaleDateString('fr-FR')}` : ' — sans limite'}
                      {' · '}
                      <span className="font-medium text-content-muted">{b.tranches.length} tranche(s)</span>
                    </p>
                  </div>

                  {/* Bouton Modifier */}
                  <IconButton
                    icon={Pencil}
                    label="Modifier le barème"
                    href={`/paie/configuration/baremes/${b.idConfigITS}/modifier`}
                  />

                  {/* Bouton voir tranches */}
                  <IconButton
                    icon={open ? ChevronDown : ChevronRight}
                    label={open ? 'Masquer les tranches' : 'Voir les tranches'}
                    onClick={() => toggle(b.idConfigITS)}
                  />
                </div>

                {/* Tranches expandables */}
                {open && (
                  <div className="px-5 pb-4">
                    <TranchesTable tranches={b.tranches} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
